import API_BASE_URL from '../../config/api';
import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import JsonViewer from '../../components/base/JsonViewer';
import { AuthContext } from '../../context/AuthContext';
import MessageModal from '../../components/base/MessageModal';
import { getReservedPdfClientError, parseClientJobFromParserName } from '../../utils/reservedPdfClients';




interface Field {
  id: string;
  name: string;
  description: string;
}

interface SubSection {
  id: string;
  name: string;
  fields: Field[];
}

interface Section {
  id: string;
  name: string;
  fields: Field[];
  subSections: SubSection[];
}

export default function CreateNew() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [parserName, setParserName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [rootFields, setRootFields] = useState<Field[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [reservedModal, setReservedModal] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const [username, setUsername] = useState<string | null>(auth?.username || localStorage.getItem('username'));

  useEffect(() => {
    if (!username && auth?.username) setUsername(auth.username);
  }, [auth, username]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addSection = () => {
    const newSection: Section = { id: generateId(), name: '', fields: [], subSections: [] };
    setSections([...sections, newSection]);
  };

  const addSubSection = (sectionId: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const newSubSection: SubSection = { id: generateId(), name: '', fields: [] };
        return { ...section, subSections: [...section.subSections, newSubSection] };
      }
      return section;
    }));
  };

  const addField = (sectionId?: string, subSectionId?: string) => {
    const newField: Field = { id: generateId(), name: '', description: '' };
    if (!sectionId && !subSectionId) {
      setRootFields([...rootFields, newField]);
    } else if (sectionId && !subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) return { ...section, fields: [...section.fields, newField] };
        return section;
      }));
    } else if (sectionId && subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) return { ...subSection, fields: [...subSection.fields, newField] };
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const deleteSection = (sectionId: string) => setSections(sections.filter(section => section.id !== sectionId));
  const deleteSubSection = (sectionId: string, subSectionId: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) return { ...section, subSections: section.subSections.filter(sub => sub.id !== subSectionId) };
      return section;
    }));
  };

  const deleteField = (fieldId: string, sectionId?: string, subSectionId?: string) => {
    if (!sectionId && !subSectionId) {
      setRootFields(rootFields.filter(field => field.id !== fieldId));
    } else if (sectionId && !subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) return { ...section, fields: section.fields.filter(field => field.id !== fieldId) };
        return section;
      }));
    } else if (sectionId && subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) return { ...subSection, fields: subSection.fields.filter(field => field.id !== fieldId) };
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const updateSectionName = (sectionId: string, name: string) => {
    setSections(sections.map(section => section.id === sectionId ? { ...section, name } : section));
  };

  const updateSubSectionName = (sectionId: string, subSectionId: string, name: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          subSections: section.subSections.map(subSection => subSection.id === subSectionId ? { ...subSection, name } : subSection)
        };
      }
      return section;
    }));
  };

  const updateField = (fieldId: string, name: string, description: string, sectionId?: string, subSectionId?: string) => {
    if (!sectionId && !subSectionId) {
      setRootFields(rootFields.map(field => field.id === fieldId ? { ...field, name, description } : field));
    } else if (sectionId && !subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.map(field => field.id === fieldId ? { ...field, name, description } : field)
          };
        }
        return section;
      }));
    } else if (sectionId && subSectionId) {
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) {
                return {
                  ...subSection,
                  fields: subSection.fields.map(field => field.id === fieldId ? { ...field, name, description } : field)
                };
              }
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const generateJsonSchema = () => {
    const schema: Record<string, any> = { document_type: documentType };
    // Always set empty string values in preview regardless of description content
    rootFields.forEach(field => field.name && (schema[field.name] = ''));
    sections.forEach(section => {
      if (section.name) {
        const sectionSchema: Record<string, any> = {};
        section.fields.forEach(field => field.name && (sectionSchema[field.name] = ''));
        section.subSections.forEach(subSection => {
          if (subSection.name) {
            const subSectionSchema: Record<string, string> = {};
            subSection.fields.forEach(field => field.name && (subSectionSchema[field.name] = ''));
            sectionSchema[subSection.name] = subSectionSchema;
          }
        });
        schema[section.name] = sectionSchema;
      }
    });
    return schema;
  };

  const renderFields = (fields: Field[], sectionId?: string, subSectionId?: string) => {
    if (fields.length === 0) return null;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 pb-2 border-b border-gray-200">
          <div className="text-sm font-medium text-indigo-600">Fields</div>
          <div className="text-sm font-medium text-indigo-600">Description</div>
        </div>
        {fields.map((field) => (
          <div key={field.id} className="grid grid-cols-2 gap-4 items-center">
            <input
              type="text"
              placeholder="Field name"
              value={field.name}
              onChange={(e) => updateField(field.id, e.target.value, field.description, sectionId, subSectionId)}
              className="w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-sm"
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Enter field description"
                value={field.description}
                onChange={(e) => updateField(field.id, field.name, e.target.value, sectionId, subSectionId)}
                className="w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-sm"
              />
              <button
                onClick={() => deleteField(field.id, sectionId, subSectionId)}
                className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const validateFields = () => {
    if (!parserName.trim()) {
      alert('Please enter a parser name');
      return false;
    }
    if (!documentType.trim()) {
      alert('Please enter a document type');
      return false;
    }
    if (!username) {
      alert('User not authenticated. Please log in.');
      return false;
    }
    const hasFields = rootFields.some(field => field.name.trim()) ||
      sections.some(section => section.fields.some(field => field.name.trim()) ||
        section.subSections.some(subSection => subSection.fields.some(field => field.name.trim())));
    if (!hasFields) {
      alert('Please add at least one field');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    setIsLoading(true);
    setSuccessMessage('');
    const jsonSchema = generateJsonSchema();
    const parserFileName = `${parserName}.json`;
    try {
      const { pdfClientName, jobType } = parseClientJobFromParserName(parserName);
      const reservedError = await getReservedPdfClientError(pdfClientName, jobType || documentType);
      if (reservedError) {
        setReservedModal({ open: true, message: reservedError });
        return;
      }

      // const response = await fetch('/api/save-parser', {
      const response = await fetch(`${API_BASE_URL}/save-parser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ parserFileName, documentType, jsonSchema }),
      });
      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Parser saved successfully');
        setTimeout(() => {
          setSuccessMessage('');
          navigate('/parser-creation');
        }, 1500);
      } else {
        const errMsg = result.error || 'Failed to save parser';
        if (/reserved/i.test(errMsg)) {
          setReservedModal({ open: true, message: errMsg });
        } else {
          alert(errMsg);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save parser. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      <div className="px-4 py-6">
        <Button variant="outline" onClick={() => navigate('/parser-creation')} className="mb-6">Back</Button>
      </div>
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center gap-2"><i className="ri-check-line"></i>{successMessage}</div>
        </div>
      )}
      <div className="flex flex-col items-center px-6">
        <div className="w-full max-w-7xl">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Create New Parser</h1>
            <div className="w-full h-px bg-gray-300"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="space-y-6">
                <Input placeholder="Enter the parser name in the format: clientname_jobtype" label="Parser Name" value={parserName} onChange={(e) => setParserName(e.target.value)} />
                <Input placeholder="Enter document type" label="Document Type" value={documentType} onChange={(e) => setDocumentType(e.target.value)} />
                <div className="space-y-4">{renderFields(rootFields)}</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => addField()} className="flex items-center gap-2 px-4 py-2 text-cyan-400 hover:text-cyan-500 cursor-pointer">
                    <i className="ri-add-line"></i><span className="text-base">Add Field</span>
                  </button>
                </div>
                {sections.map((section) => (
                  <div key={section.id} className="border-2 border-gray-300 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Section Name"
                          value={section.name}
                          onChange={(e) => updateSectionName(section.id, e.target.value)}
                          className="px-3 py-1 border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-600 text-base"
                        />
                        <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs">Section</span>
                      </div>
                      <button onClick={() => deleteSection(section.id)} className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                    <div className="space-y-4 mb-4">{renderFields(section.fields, section.id)}</div>
                    {section.subSections.map((subSection) => (
                      <div key={subSection.id} className="ml-6 border-l-2 border-gray-200 pl-4 mb-4">
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Sub-section Name"
                              value={subSection.name}
                              onChange={(e) => updateSubSectionName(section.id, subSection.id, e.target.value)}
                              className="px-3 py-1 border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-600 text-base"
                            />
                          </div>
                          <button onClick={() => deleteSubSection(section.id, subSection.id)} className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                        <div className="space-y-4 mb-4">{renderFields(subSection.fields, section.id, subSection.id)}</div>
                        <button onClick={() => addField(section.id, subSection.id)} className="flex items-center gap-2 px-4 py-2 text-cyan-400 hover:text-cyan-500 cursor-pointer">
                          <i className="ri-add-line"></i><span className="text-base">Add Field</span>
                        </button>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => addField(section.id)} className="flex items-center gap-2 px-4 py-2 text-cyan-400 hover:text-cyan-500 cursor-pointer">
                        <i className="ri-add-line"></i><span className="text-base">Add Field</span>
                      </button>
                      <button onClick={() => addSubSection(section.id)} className="flex items-center gap-2 px-4 py-2 text-cyan-400 hover:text-cyan-500 cursor-pointer">
                        <i className="ri-add-line"></i><span className="text-base">Add Sub Section</span>
                      </button>
                    </div>
                  </div>
                ))}
                <button onClick={addSection} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-400 text-white rounded-lg hover:bg-cyan-500 transition-colors">
                  <i className="ri-add-line"></i><span>Add Section</span>
                </button>
                <div className="flex space-x-4">
                  <Button onClick={handleSave} variant="primary" disabled={isLoading} className="whitespace-nowrap">
                    {isLoading ? (<><i className="ri-loader-4-line animate-spin mr-2"></i>Saving...</>) : 'Save'}
                  </Button>
                  <Button onClick={() => navigate('/parser-creation')} variant="outline">Cancel</Button>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <JsonViewer data={generateJsonSchema()} />
            </div>
          </div>
        </div>
      </div>

      <MessageModal
        open={reservedModal.open}
        title="Reserved Format Name"
        message={reservedModal.message}
        variant="error"
        onClose={() => setReservedModal({ open: false, message: '' })}
      />
    </div>
  );
}

